import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Pagination,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaUser,
  FaUsers,
  FaSearch,
  FaFilter,
  FaEye,
  FaTrash,
  FaCheck,
  FaTimes,
  FaBan,
  FaChartBar,
  FaCalendar,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaFileExcel,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import * as XLSX from "xlsx";

const UserDashboard = () => {
  const navigate = useNavigate();
  const { isSubadmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch users from backend
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);

        // Get admin token from session storage
        const adminToken = sessionStorage.getItem("adminToken");

        // Use dynamic base URL approach like other admin components
        const bases = Array.from(
          new Set(
            [
              process.env.REACT_APP_API_BASE_URL,
              "https://api.quvikme.com",
            ].filter(Boolean)
          )
        );

        let data = null;
        let lastErr = null;

        // Try to fetch users from each base URL until one works
        for (const base of bases) {
          try {
            const res = await fetch(`${base}/api/users?limit=1000`, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
            });

            if (!res.ok) {
              lastErr = new Error(`Failed to load users from ${base}`);
              continue;
            }

            data = await res.json();
            break;
          } catch (e) {
            lastErr = e;
            continue;
          }
        }

        if (!data)
          throw lastErr || new Error("Failed to load users from any endpoint");

        // Log the raw data for debugging
        console.log("Raw users data from backend:", data);

        // Normalize to expected fields
        const mapped = (data.users || []).map((u) => ({
          id: u.id || u._id,
          firstName: (u.name || "").split(" ")[0] || "",
          lastName: (u.name || "").split(" ").slice(1).join(" ") || "",
          email: u.email || "",
          phone: u.phone || "",

          joinDate: u.createdAt || new Date().toISOString(),
          location: u.address || "N/A",
          pincode: u.pincode || "N/A", // Add pincode field
          societyName: u.societyName || "N/A", // Add societyName field
          totalBookings: u.totalBookings || 0,
          totalSpent: u.totalSpent || 0,
          totalTax: u.totalTax || 0, // Add totalTax field
          lastActive:
            u.lastLogin ||
            u.updatedAt ||
            u.createdAt ||
            new Date().toISOString(),

          preferences: Array.isArray(u.preferences) ? u.preferences : [],
          paymentMethods: Array.isArray(u.paymentMethods)
            ? u.paymentMethods
            : [],
        }));

        // Log the mapped data for debugging
        console.log("Mapped users data:", mapped);

        setUsers(mapped);
        setFilteredUsers(mapped);

        // Set totals from backend response if provided
        if (data.totalBookings !== undefined && data.totalSpent !== undefined) {
          setTotalBookingsFromBackend(data.totalBookings);
          setTotalSpentFromBackend(data.totalSpent);
          setTotalTaxFromBackend(data.totalTax || 0); // Set total tax from backend
        } else {
          // Fallback to calculating from individual user data
          const calculatedTotalBookings = mapped.reduce(
            (sum, u) => sum + (u.totalBookings || 0),
            0
          );
          const calculatedTotalSpent = mapped.reduce(
            (sum, u) => sum + (u.totalSpent || 0),
            0
          );
          const calculatedTotalTax = mapped.reduce(
            (sum, u) => sum + (u.totalTax || 0),
            0
          ); // Calculate total tax
          setTotalBookingsFromBackend(calculatedTotalBookings);
          setTotalSpentFromBackend(calculatedTotalSpent);
          setTotalTaxFromBackend(calculatedTotalTax); // Set calculated total tax
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // State for backend totals
  const [totalBookingsFromBackend, setTotalBookingsFromBackend] = useState(0);
  const [totalSpentFromBackend, setTotalSpentFromBackend] = useState(0);
  const [totalTaxFromBackend, setTotalTaxFromBackend] = useState(0); // Add state for total tax

  // Filter and search functionality
  useEffect(() => {
    let filtered = users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const matchesSearch =
        fullName.includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.pincode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.societyName.toLowerCase().includes(searchTerm.toLowerCase()); // Add societyName to search

      // Remove status filter since we're removing status functionality
      return matchesSearch;
    });

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Statistics - remove status-related statistics
  const stats = {
    total: users.length,
    // Remove status counts
    totalBookings: totalBookingsFromBackend,
    totalSpent: totalSpentFromBackend,
    totalTax: totalTaxFromBackend, // Add total tax to stats
    avgBookings:
      users.length > 0
        ? (totalBookingsFromBackend / users.length).toFixed(1)
        : 0,
  };

  // Action handlers
  const handleViewUser = (user) => {
    navigate(`/user-info/${user.id}`);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      // Use dynamic base URL approach
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let success = false;
      let lastErr = null;

      // Try each base URL until one works
      for (const base of bases) {
        try {
          const url = `${base}/api/users/${selectedUser.id}`;

          console.log("🗑️ Deleting user:", selectedUser.id);

          // Get admin token for authentication
          const adminToken = sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };

          // Add authorization header if token exists
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const response = await fetch(url, {
            method: "DELETE",
            headers: headers,
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            lastErr = new Error(
              errorData.message ||
                `Failed to delete user: ${response.status} ${response.statusText}`
            );
            continue;
          }

          const result = await response.json();
          console.log("✅ User deleted successfully:", result);
          success = true;
          break;
        } catch (error) {
          lastErr = error;
          continue;
        }
      }

      if (!success)
        throw lastErr || new Error("Failed to delete user from any endpoint");

      // Remove user from local state
      const updatedUsers = users.filter((u) => u.id !== selectedUser.id);
      setUsers(updatedUsers);
      setShowDeleteModal(false);
      setSelectedUser(null);

      // Recalculate totals
      const updatedTotalBookings = updatedUsers.reduce(
        (sum, u) => sum + (u.totalBookings || 0),
        0
      );
      const updatedTotalSpent = updatedUsers.reduce(
        (sum, u) => sum + (u.totalSpent || 0),
        0
      );
      const updatedTotalTax = updatedUsers.reduce(
        (sum, u) => sum + (u.totalTax || 0),
        0
      ); // Calculate updated total tax
      setTotalBookingsFromBackend(updatedTotalBookings);
      setTotalSpentFromBackend(updatedTotalSpent);
      setTotalTaxFromBackend(updatedTotalTax); // Set updated total tax

      // Show success message
      setErrorMessage("");
      setSuccessMessage(
        `User "${selectedUser.firstName} ${selectedUser.lastName}" has been deleted successfully.`
      );

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("❌ Error deleting user:", error);
      setErrorMessage(`Failed to delete user: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getLastActiveBadge = (lastActive) => {
    const daysAgo = Math.floor(
      (new Date() - new Date(lastActive)) / (1000 * 60 * 60 * 24)
    );
    if (daysAgo === 0) return <Badge bg="success">Today</Badge>;
    if (daysAgo === 1) return <Badge bg="info">Yesterday</Badge>;
    if (daysAgo < 7) return <Badge bg="warning">{daysAgo} days ago</Badge>;
    return <Badge bg="secondary">{daysAgo} days ago</Badge>;
  };

  // Download users report as Excel
  const downloadUsersReport = () => {
    // Prepare data for export
    const reportData = users.map((user) => ({
      "First Name": user.firstName,
      "Last Name": user.lastName,
      Email: user.email,
      Phone: user.phone,
      Location: user.location,
      Pincode: user.pincode,
      "Society Name": user.societyName, // Add societyName to export
      "Total Bookings": user.totalBookings,
      "Total Spent ($)": user.totalSpent,
      "Total Tax ($)": user.totalTax, // Add totalTax to export
      "Join Date": new Date(user.joinDate).toLocaleDateString(),
      "Last Active": new Date(user.lastActive).toLocaleDateString(),
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(reportData);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users Report");

    // Export to Excel file
    XLSX.writeFile(wb, "users-report.xlsx");
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold">
            <FaUser className="me-2" />
            User Management Dashboard
          </h2>
          <p className="text-muted">
            Manage all registered users and their activities
          </p>
        </Col>
      </Row>

      {/* Error Alert */}
      {errorMessage && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant="danger"
              dismissible
              onClose={() => setErrorMessage("")}
            >
              <FaTimes className="me-2" />
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant="success"
              dismissible
              onClose={() => setSuccessMessage("")}
            >
              <FaCheck className="me-2" />
              {successMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Statistics Cards */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaUsers className="text-primary mb-2" size={28} />
              <h3 className="fw-bold mb-1">{stats.total}</h3>
              <p className="text-muted small mb-0">Total Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaCalendar className="text-info mb-2" size={28} />
              <h3 className="fw-bold mb-1">{stats.totalBookings}</h3>
              <p className="text-muted small mb-0">Total Bookings</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaChartBar className="text-warning mb-2" size={28} />
              <h3 className="fw-bold mb-1">₹{stats.totalSpent.toLocaleString()}</h3>
              <p className="text-muted small mb-0">Total Spent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center shadow-sm border-0 h-100">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
              <FaChartBar className="text-success mb-2" size={28} />
              <h3 className="fw-bold mb-1">₹{stats.totalTax.toLocaleString()}</h3>
              <p className="text-muted small mb-0">Total Tax</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Row className="g-3 mb-4">
        <Col xs={12} md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search users by name, email, location, pincode, or society name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6}>{/* Status filter removed */}</Col>
      </Row>

      {/* Users Table */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">All Users ({filteredUsers.length})</h5>
          <Button variant="outline-success" onClick={downloadUsersReport}>
            <FaFileExcel className="me-2" />
            Export to Excel
          </Button>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Location</th>
                <th>Pincode</th>
                <th>Society Name</th> {/* Add Society Name column */}
                <th>Bookings</th>
                <th>Total Spent</th>
                <th>Tax</th> {/* Add Tax column */}
                <th>Join Date</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <strong>
                        {user.firstName} {user.lastName}
                      </strong>
                      <br />
                      <small className="text-muted">
                        <FaPhone className="me-1" />
                        {user.phone}
                      </small>
                      <br />
                    </div>
                  </td>
                  <td>
                    <small className="text-muted">
                      <FaEnvelope className="me-1" />
                      {user.email}
                    </small>
                  </td>
                  <td>
                    <FaMapMarkerAlt className="me-1 text-muted" />
                    {user.location}
                  </td>
                  <td>{user.pincode}</td>
                  <td>{user.societyName}</td> {/* Add Society Name data */}
                  <td>{user.totalBookings}</td>
                  <td>₹{user.totalSpent.toLocaleString()}</td>
                  <td>₹{user.totalTax.toLocaleString()}</td>{" "}
                  {/* Add Tax data */}
                  <td>{new Date(user.joinDate).toLocaleDateString()}</td>
                  <td>{getLastActiveBadge(user.lastActive)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleViewUser(user)}
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteUser(user)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center">
              <Pagination>
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                />

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                )}

                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* View User Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Row>
              <Col md={6}>
                <h6>Personal Information</h6>
                <p>
                  <strong>Name:</strong> {selectedUser.firstName}{" "}
                  {selectedUser.lastName}
                </p>
                <p>
                  <strong>Email:</strong> {selectedUser.email}
                </p>
                <p>
                  <strong>Phone:</strong> {selectedUser.phone}
                </p>
                <p>
                  <strong>Location:</strong> {selectedUser.location}
                </p>
                <p>
                  <strong>Pincode:</strong> {selectedUser.pincode}
                </p>
                <p>
                  <strong>Society Name:</strong> {selectedUser.societyName}
                </p>{" "}
                {/* Add Society Name to modal */}
                <p>
                  <strong>Join Date:</strong>{" "}
                  {new Date(selectedUser.joinDate).toLocaleDateString()}
                </p>
                <p>
                  <strong>Last Active:</strong>{" "}
                  {new Date(selectedUser.lastActive).toLocaleDateString()}
                </p>
              </Col>
              <Col md={6}>
                <h6>Activity & Preferences</h6>
                <p>
                  <strong>Total Bookings:</strong> {selectedUser.totalBookings}
                </p>
                <p>
                  <strong>Total Spent:</strong> ₹
                  {selectedUser.totalSpent.toLocaleString()}
                </p>
                <p>
                  <strong>Total Tax:</strong> ₹
                  {selectedUser.totalTax.toLocaleString()}
                </p>{" "}
                {/* Add Total Tax to modal */}
                <h6 className="mt-3">Service Preferences</h6>
                <div>
                  {selectedUser.preferences.map((pref, index) => (
                    <Badge key={index} bg="info" className="me-1 mb-1">
                      {pref}
                    </Badge>
                  ))}
                </div>
                <h6 className="mt-3">Subscription</h6>
                <div>
                  {selectedUser.paymentMethods.length > 0 ? (
                    selectedUser.paymentMethods.map((method, index) => (
                      <Badge key={index} bg="success" className="me-1 mb-1">
                        {method}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted">No payment methods added</span>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>⚠️ Warning: This action will permanently delete:</strong>
          </div>
          <ul className="mb-3">
            <li>User profile and all personal information</li>
            <li>All user bookings and booking history</li>
            <li>All user reviews and ratings</li>
            <li>All favorite vendors and preferences</li>
            <li>All payment methods and billing information</li>
            <li>All related data across the system</li>
          </ul>
          <div className="mb-3">
            Are you sure you want to delete{" "}
            <strong>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>
            ?
            <br />
            <strong className="text-danger">
              This action cannot be undone.
            </strong>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserDashboard;
